import clsx from "clsx";
import { motion } from "framer-motion";
import { FC, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useNavigationItems } from "@/config/navigation";
import { useMediaQuery } from "usehooks-ts";
import { cn } from "@cronika/ui";

export const SidebarMobile: FC = () => {
  const items = useNavigationItems()
    .filter((item) => !item.hiddenOnMobile)
    .sort((a, b) => a.mobileOrder - b.mobileOrder);
  const isHorizontal = useMediaQuery("(max-height: 390px)");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isOnboardingLocked, setIsOnboardingLocked] = useState(false);
  const openCountRef = useRef(0);

  useEffect(() => {
    const onOpen = () => {
      openCountRef.current++;
      setIsModalOpen(true);
    };
    const onClose = () => {
      openCountRef.current = Math.max(0, openCountRef.current - 1);
      setIsModalOpen(openCountRef.current > 0);
    };
    window.addEventListener("app:modal-open", onOpen);
    window.addEventListener("app:modal-close", onClose);
    return () => {
      window.removeEventListener("app:modal-open", onOpen);
      window.removeEventListener("app:modal-close", onClose);
    };
  }, []);

  useEffect(() => {
    const handleLock = (event: Event) => {
      setIsOnboardingLocked(Boolean((event as CustomEvent<boolean>).detail));
    };
    window.addEventListener("cronika:onboarding-lock-navigation", handleLock);
    return () => window.removeEventListener("cronika:onboarding-lock-navigation", handleLock);
  }, []);

  return (
    <motion.div
      initial={false}
      animate={
        !isHorizontal
          ? {
              opacity: isModalOpen ? 0 : 1,
              scale: isModalOpen ? 0.94 : 1,
              y: isModalOpen ? 8 : 0,
            }
          : { opacity: 1, scale: 1, y: 0 }
      }
      transition={
        !isHorizontal
          ? {
              duration: isModalOpen ? 0.18 : 0.28,
              ease: isModalOpen ? [0.4, 0, 1, 1] : [0, 0, 0.2, 1],
              delay: isModalOpen ? 0.04 : 0,
            }
          : { duration: 0 }
      }
      style={isModalOpen && !isHorizontal ? { pointerEvents: "none" } : undefined}
      className={cn(
        "bg-[#141414]/80 backdrop-blur-xl z-40 w-full flex lg:hidden rounded-[150px] max-h-12.5 items-center shadow-[inset_0.7px_0.7px_1px_rgba(255,255,255,0.2),inset_-1px_-1px_0.9px_rgba(0,0,0,0.5),0_8px_32px_rgba(0,0,0,0.25)]",
        isOnboardingLocked && "pointer-events-none select-none opacity-45",
        isHorizontal &&
          "flex-col shadow-none bg-[#070707] rounded-none h-full max-h-97.5 w-18 justify-start gap-5 py-1.25",
      )}
    >
      {items.map((item) => {
        const inner = (
          <div
            className={cn(
              "md:py-5 w-full flex flex-col justify-center items-center rounded-[80px] gap-0.5",
              isHorizontal ? "py-0" : "py-3.75",
              item.active
                ? "h-11.5 bg-linear-to-b from-[#ffffff05] to-[#ffffff10] backdrop-blur-xl border border-white/10 shadow-[inset_0.7px_0.7px_1px_rgba(255,255,255,0.6),inset_-1px_-1px_0.9px_rgba(0,0,0,0.5),0_8px_32px_rgba(0,0,0,0.25)]"
                : "bg-transparent",
              isHorizontal && "w-18 h-11.5 rounded-[10px]",
            )}
          >
            <div className="relative flex flex-col items-center gap-0.5 w-full min-w-0">
              <div className={clsx("flex flex-col items-center gap-0.5 w-full min-w-0", item.disabled && "opacity-40")}>
                <item.icon className={clsx("w-5 h-5 shrink-0", item.active ? "text-[#FEC201]" : "text-[#8C8C8C]")} />
                <p
                  className={clsx(
                    "text-[10px] leading-tight max-w-12 min-w-0 line-clamp-2 break-words text-center",
                    item.active ? "text-[#FEC201]" : "text-[#8C8C8C]",
                  )}
                >
                  {item.title}
                </p>
              </div>
              {item.disabled && (
                <span className="absolute -right-1 text-[8px] font-semibold bg-warning text-black px-1 py-0.5 rounded-full leading-none">
                  soon
                </span>
              )}
            </div>
          </div>
        );

        if (item.disabled) {
          return (
            <div key={item.key} className={cn("flex-1 min-w-0 cursor-not-allowed", isHorizontal && "max-h-10")}>
              {inner}
            </div>
          );
        }

        return (
          <Link key={item.key} to={item.link} className={cn("flex-1 min-w-0", isHorizontal && "max-h-10")}>
            {inner}
          </Link>
        );
      })}
    </motion.div>
  );
};
